import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportPreviewModal = ({ report, isOpen, onClose, onExport, onValidate }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [validationResults, setValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  if (!isOpen || !report) return null;

  const tabs = [
    { id: 'summary', label: 'Executive Summary', icon: 'FileText' },
    { id: 'data', label: 'Pay Gap Data', icon: 'BarChart3' },
    { id: 'methodology', label: 'Methodology', icon: 'Settings' },
    { id: 'annexes', label: 'Annexes', icon: 'Paperclip' }
  ];

  const handleValidation = async () => {
    setIsValidating(true);
    // Simulate validation process
    setTimeout(() => {
      setValidationResults({
        isValid: true,
        completeness: 98,
        issues: [
          { type: 'warning', message: 'Missing optional demographic breakdown for age groups 55+' },
          { type: 'info', message: 'Consider adding trend analysis for better context' }
        ],
        checkedItems: [
          'Pay gap calculations verified',
          'Statistical significance confirmed',
          'Data quality checks passed',
          'Regulatory requirements met',
          'Audit trail complete'
        ]
      });
      setIsValidating(false);
    }, 2000);
  };

  const renderSummaryTab = () => (
    <div className="space-y-6">
      <div className="bg-muted/50 p-4 rounded-lg">
        <h3 className="font-semibold text-foreground mb-3">Executive Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Overall Gender Pay Gap:</span>
              <span className="font-medium text-foreground">12.3%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Median Pay Gap:</span>
              <span className="font-medium text-foreground">8.7%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Bonus Gap:</span>
              <span className="font-medium text-foreground">15.2%</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Employees:</span>
              <span className="font-medium text-foreground">2,847</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Female Representation:</span>
              <span className="font-medium text-foreground">42.1%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reporting Period:</span>
              <span className="font-medium text-foreground">{report?.period}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="prose prose-sm max-w-none">
        <p className="text-muted-foreground">
          This report presents our organization's gender pay gap analysis for {report?.period} in compliance with the EU Pay Transparency Directive. 
          The analysis covers all employees within our {report?.scope} and demonstrates our commitment to pay equity and transparency.
        </p>
        <p className="text-muted-foreground">
          Key findings indicate a 12.3% overall gender pay gap, which represents a 2.1% improvement from the previous reporting period. 
          Our organization has implemented targeted remediation measures including pay band restructuring and enhanced promotion processes.
        </p>
      </div>
    </div>
  );

  const renderDataTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium text-foreground mb-3">Pay Gap by Quartile</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Upper Quartile</span>
              <span className="text-sm font-medium text-destructive">18.5%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Upper Middle</span>
              <span className="text-sm font-medium text-warning">11.2%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Lower Middle</span>
              <span className="text-sm font-medium text-success">6.8%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Lower Quartile</span>
              <span className="text-sm font-medium text-success">3.1%</span>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium text-foreground mb-3">Department Breakdown</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Engineering</span>
              <span className="text-sm font-medium text-destructive">16.3%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Sales</span>
              <span className="text-sm font-medium text-warning">9.7%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Marketing</span>
              <span className="text-sm font-medium text-success">4.2%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Operations</span>
              <span className="text-sm font-medium text-success">2.8%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-medium text-foreground mb-3">Statistical Significance</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">95%</div>
            <div className="text-muted-foreground">Confidence Level</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">±2.1%</div>
            <div className="text-muted-foreground">Margin of Error</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">2,847</div>
            <div className="text-muted-foreground">Sample Size</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMethodologyTab = () => (
    <div className="space-y-4">
      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-medium text-foreground mb-3">Calculation Methodology</h4>
        <div className="prose prose-sm max-w-none text-muted-foreground">
          <ul className="space-y-2">
            <li>• Pay gap calculated as (Male Median - Female Median) / Male Median × 100</li>
            <li>• Includes base salary, bonuses, and variable compensation</li>
            <li>• Excludes overtime, benefits, and equity compensation</li>
            <li>• Data normalized to full-time equivalent positions</li>
            <li>• Statistical significance tested using t-test methodology</li>
          </ul>
        </div>
      </div>
      
      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-medium text-foreground mb-3">Data Sources</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Database" size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">HRIS System (Workday) - Employee demographics and compensation</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="FileSpreadsheet" size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">Payroll System - Actual compensation data</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">Time Tracking - FTE calculations</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnnexesTab = () => (
    <div className="space-y-4">
      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-medium text-foreground mb-3">Supporting Documentation</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-card border border-border rounded">
            <div className="flex items-center space-x-3">
              <Icon name="FileText" size={16} className="text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-foreground">Detailed Statistical Analysis</div>
                <div className="text-xs text-muted-foreground">Complete breakdown by department and level</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" iconName="Download">Download</Button>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-card border border-border rounded">
            <div className="flex items-center space-x-3">
              <Icon name="BarChart3" size={16} className="text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-foreground">Data Visualization Package</div>
                <div className="text-xs text-muted-foreground">Charts and graphs for presentation</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" iconName="Download">Download</Button>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-card border border-border rounded">
            <div className="flex items-center space-x-3">
              <Icon name="Shield" size={16} className="text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-foreground">Compliance Checklist</div>
                <div className="text-xs text-muted-foreground">EU Directive requirements verification</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" iconName="Download">Download</Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">{report?.name}</h2>
              <p className="text-sm text-muted-foreground">Report Preview & Validation</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-smooth ${
                activeTab === tab?.id
                  ? 'border-b-2 border-primary text-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'summary' && renderSummaryTab()}
          {activeTab === 'data' && renderDataTab()}
          {activeTab === 'methodology' && renderMethodologyTab()}
          {activeTab === 'annexes' && renderAnnexesTab()}
        </div>

        {/* Validation Results */}
        {validationResults && (
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-foreground">Validation Complete</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Report completeness: {validationResults?.completeness}%
                </p>
                {validationResults?.issues?.length > 0 && (
                  <div className="space-y-1">
                    {validationResults?.issues?.map((issue, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <Icon 
                          name={issue?.type === 'warning' ? 'AlertTriangle' : 'Info'} 
                          size={12} 
                          className={issue?.type === 'warning' ? 'text-warning' : 'text-primary'} 
                        />
                        <span className="text-muted-foreground">{issue?.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={14} />
            <span>EU Directive Compliant • GDPR Secure</span>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              iconName="CheckCircle"
              iconPosition="left"
              loading={isValidating}
              onClick={handleValidation}
            >
              {isValidating ? 'Validating...' : 'Validate Report'}
            </Button>
            <Button
              variant="default"
              iconName="Download"
              iconPosition="left"
              onClick={() => onExport(report)}
            >
              Export Report
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewModal;